import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import styled from "styled-components";
import MoveIcon from '@mui/icons-material/CompareArrows';
import axios from 'axios';
import constants from '../../const.js';

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 760px;
    width: 95vw;
  }

  .modal-content {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: none;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 20px;
  }

  .modal-header .modal-title {
    font-weight: 700;
    font-size: 18px;
    color: white;
  }

  .modal-header .btn-close {
    filter: brightness(0) invert(1);
  }

  .modal-body {
    background: #f8f9fb;
    padding: 20px;
    max-height: 85vh;
    overflow-y: auto;
  }

  .modal-footer {
    background: #f8f9fb;
    border-top: 1px solid #e0e0e0;
    padding: 12px 20px;
  }
`;

const ModernFormGroup = styled(Form.Group)`
  margin-bottom: 16px;

  .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-control {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: #ffffff;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.1rem rgba(102, 126, 234, 0.15);
      background-color: #ffffff;
    }
  }
`;

const ModernButton = styled(Button)`
  border-radius: 6px;
  font-weight: 600;
  padding: 8px 16px;
  transition: all 0.3s ease;
  border: none;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.btn-secondary {
    background: #95a5a6;
    color: white;

    &:hover:not(:disabled) {
      background: #7f8c8d;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const ImagePreview = styled.div`
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  .image-name {
    font-size: 12px;
    color: #666;
    word-break: break-all;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin: 12px 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AlbumPickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  max-height: 360px;
  overflow-y: auto;
  padding: 4px;
`;

const AlbumTile = styled.button`
  display: flex;
  flex-direction: column;
  padding: 6px;
  background: white;
  border: 2px solid ${props => (props.$selected ? '#667eea' : '#e0e0e0')};
  border-radius: 8px;
  box-shadow: ${props => (props.$selected ? '0 0 0 3px rgba(102, 126, 234, 0.25)' : 'none')};
  cursor: pointer;
  text-align: left;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: #667eea;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .album-tile-poster {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border-radius: 4px;
    background: #eef0f5;
    color: #999;
    font-size: 12px;
  }

  .album-tile-title {
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .album-tile-count {
    font-size: 11px;
    color: #666;
  }
`;

function AlbumPoster({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className="album-tile-poster">No poster</div>;
  }
  return (
    <img
      className="album-tile-poster"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function MoveImageModal(props) {
  // Controlled mode: the parent passes `open` and `onClose`, and can set `hideTrigger`
  const isControlled = props.open !== undefined;
  const [internalShow, setInternalShow] = useState(false);
  const show = isControlled ? props.open : internalShow;
  const setShow = (value) => {
    if (!isControlled) setInternalShow(value);
  };
  const [loading, setLoading] = useState(false);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [error, setError] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [targetAlbum, setTargetAlbum] = useState('');

  // Handle both single and multiple images
  const isMultiSelect = props.isMultiSelect || false;
  const imageNames = isMultiSelect ? props.selectedImages : (props.imageName ? [props.imageName] : []);
  const displayTitle = isMultiSelect 
    ? `Move ${imageNames.length} images to another album`
    : 'Move Image to Another Album';

  const [albumSearch, setAlbumSearch] = useState('');
  const posterToken = localStorage.getItem('token')?.slice(7) || '';

  const albumPosterUrl = (folderName) =>
    `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${encodeURIComponent(props.galleryId)}/${encodeURIComponent(folderName)}/preview.webp?token=${encodeURIComponent(posterToken)}`;

  const searchTerm = albumSearch.trim().toLowerCase();
  const filteredAlbums = albums.filter(album =>
    !searchTerm ||
    (album.title || '').toLowerCase().includes(searchTerm) ||
    album.folder_name.toLowerCase().includes(searchTerm)
  );
  const selectedAlbum = albums.find(album => album.folder_name === targetAlbum);

  const handleShow = () => {
    setShow(true);
    setError(null);
    setTargetAlbum('');
    fetchAlbums();
  };

  const handleClose = () => {
    setShow(false);
    setError(null);
    if (props.onClose) props.onClose();
  };

  useEffect(() => {
    if (isControlled && props.open) {
      setError(null);
      setTargetAlbum('');
      fetchAlbums();
    }
  }, [isControlled, props.open]);

  const fetchAlbums = async () => {
    try {
      setLoadingAlbums(true);
      setAlbumSearch('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.get(
        `${constants.DATA1_API_URL}/gallery/${encodeURIComponent(props.galleryId)}/albums`,
        {
          headers: {
            'Authorization': token
          }
        }
      );

      if (response.data && Array.isArray(response.data)) {
        // Filter out the current album
        const filteredAlbums = response.data.filter(
          album => album.folder_name !== props.currentAlbumId
        );
        setAlbums(filteredAlbums);
        
        if (filteredAlbums.length > 0) {
          setTargetAlbum(filteredAlbums[0].folder_name);
        }
      }
    } catch (err) {
      console.error('Error fetching albums:', err);
      setError('Failed to load albums');
    } finally {
      setLoadingAlbums(false);
    }
  };

  const handleMoveImage = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!targetAlbum) {
        setError('Please select a target album');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Call API to move images (works for single or multiple)
      const response = await axios.post(
        `${constants.DATA1_API_URL}/gallery/move-images`,
        {
          galleryFolderName: props.galleryId,
          sourceAlbumName: props.currentAlbumId,
          targetAlbumName: targetAlbum,
          imageNames: imageNames
        },
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        console.log('✓ Images moved successfully:', response.data);
        handleClose();
        
        // Call the onSave callback to refresh the gallery
        if (props.onSave) {
          props.onSave();
        }
      } else {
        setError('Failed to move images: ' + (response.data?.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error moving images:', err);
      setError(err.response?.data?.error || err.message || 'Failed to move images');
    } finally {
      setLoading(false);
    }
  };

  const token = localStorage.getItem('token').slice(7);
  const imageUrl = props.imageName && token 
    ? `${constants.CONTENT_DOMAIN}download/sus-media/galleries/${encodeURIComponent(props.galleryId)}/${encodeURIComponent(props.currentAlbumId)}/${encodeURIComponent(props.imageName)}?token=${encodeURIComponent(token)}`
    : '';

  return (
    <>
      {props.hideTrigger ? null : isMultiSelect ? (
        <Button
          variant="primary"
          size="sm"
          onClick={handleShow}
          title="Move selected images to another album"
          style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600' }}
        >
          <MoveIcon style={{ fontSize: '16px', marginRight: '4px' }} />
          Bulk Move ({imageNames.length})
        </Button>
      ) : (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleShow}
          title="Move this image to another album"
          className="ms-2"
        >
          <MoveIcon style={{ fontSize: '16px' }} />
        </Button>
      )}

      <StyledModal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{displayTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {!isMultiSelect && (
            <>
              <SectionTitle>Image Details</SectionTitle>
              
              <ImagePreview>
                <img 
                  src={imageUrl} 
                  alt={props.imageName}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="image-name">{props.imageName}</div>
              </ImagePreview>
            </>
          )}

          {isMultiSelect && (
            <>
              <SectionTitle>Selected Images ({imageNames.length})</SectionTitle>
              <div style={{
                backgroundColor: '#f8f9fb',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '12px',
                maxHeight: '150px',
                overflowY: 'auto',
                marginBottom: '16px'
              }}>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {imageNames.map((name, idx) => (
                    <li key={idx} style={{ fontSize: '12px', color: '#666', marginBottom: '4px', wordBreak: 'break-all' }}>
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <SectionTitle>Select Target Album</SectionTitle>
          
          {loadingAlbums ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner animation="border" size="sm" />
              <p style={{ marginTop: '10px' }}>Loading albums...</p>
            </div>
          ) : (
            <ModernFormGroup>
              <Form.Label>Target Album</Form.Label>
              <Form.Control
                              type="search"
                              placeholder="Search by title or folder name"
                              value={albumSearch}
                              onChange={(e) => setAlbumSearch(e.target.value)}
                              disabled={loading || albums.length === 0}
                            />
                            {albums.length === 0 ? (
                              <small className="text-muted">
                                No other albums available in this gallery
                              </small>
                            ) : filteredAlbums.length === 0 ? (
                              <small className="text-muted">No albums match "{albumSearch}"</small>
                            ) : (
                              <AlbumPickGrid className="mt-2">
                                {filteredAlbums.map(album => (
                                  <AlbumTile
                                    key={album.album_id}
                                    type="button"
                                    $selected={targetAlbum === album.folder_name}
                                    onClick={() => setTargetAlbum(album.folder_name)}
                                    disabled={loading}
                                    title={album.title || album.folder_name}
                                  >
                                    <AlbumPoster src={albumPosterUrl(album.folder_name)} alt={album.title || album.folder_name} />
                                    <span className="album-tile-title">{album.title || album.folder_name}</span>
                                    <span className="album-tile-count">{album.image_count || 0} images</span>
                                  </AlbumTile>
                                ))}
                              </AlbumPickGrid>
                            )}
                            {selectedAlbum && (
                              <small className="d-block mt-2 text-muted">
                                Moving to: <strong>{selectedAlbum.title || selectedAlbum.folder_name}</strong>
                              </small>
                            )}
            </ModernFormGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ModernButton 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </ModernButton>
          <ModernButton 
            variant="primary" 
            onClick={handleMoveImage}
            disabled={loading || !targetAlbum || albums.length === 0}
          >
            {loading ? (
              <>
                <Spinner 
                  as="span" 
                  animation="border" 
                  size="sm" 
                  role="status" 
                  aria-hidden="true"
                  style={{ marginRight: '8px' }}
                />
                Moving...
              </>
            ) : (
              <>
                <MoveIcon style={{ fontSize: '18px' }} />
                Move Image
              </>
            )}
          </ModernButton>
        </Modal.Footer>
      </StyledModal>
    </>
  );
}

export default MoveImageModal;
